<template>
  <div
    class="info-box-wrapper"
    ref="wrapper">
    <div
      v-if="isVisible"
      :class="cssClasses"
      @click.stop
      :style="boxStyles">
      <div class="info-box-content">
        <slot name="title"></slot>
        <slot name="content"></slot>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'info-box',
  props: {
    position: {
      type: String,
      default: 'top'
    },
    spacing: {
      type: Number,
      default: 40
    },
    size: {
      type: String,
      default: ''
    },
    heightLimit: {
      type: Boolean,
      default: true
    },
    parentSelectorWithOverflow: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      isVisible: false,
      finalPosition: ''
    };
  },
  computed: {
    boxStyles () {
      if (this.finalPosition === 'right') {
        return 'left: ' + this.spacing + 'px;';
      }

      if (this.finalPosition === 'top' || this.finalPosition === 'top-left') {
        return 'bottom: ' + this.spacing + 'px;';
      }

      if (this.finalPosition === 'bottom' || this.finalPosition === 'bottom-left') {
        return 'top: ' + this.spacing + 'px;';
      }
    },
    cssClasses () {
      return {
        'info-box': true,
        'is-right': this.finalPosition === 'right',
        'is-bottom': this.finalPosition === 'bottom',
        'is-bottom-left': this.finalPosition === 'bottom-left',
        'is-top': this.finalPosition === 'top',
        'is-top-left': this.finalPosition === 'top-left',
        'is-bigger': this.size === 'bigger',
        'is-biggest': this.size === 'biggest',
        'has-height-limit': this.heightLimit,
        'is-visible': this.finalPosition !== ''
      };
    }
  },
  mounted () {
    this.$bus.$on('body-clicked', this.hide);
    this.$bus.$on('popup-clicked', this.hide);
  },
  methods: {
    show () {
      this.isVisible = true;

      setTimeout(() => {
        if (this.parentSelectorWithOverflow) {
          let parentWithOverflow = document.querySelector(this.parentSelectorWithOverflow);
          let infoBoxRect = this.$refs['wrapper'].querySelector('.info-box').getBoundingClientRect();
          let parentRect = parentWithOverflow.getBoundingClientRect();

          if (this.position === 'right') {
            let infoBoxTop = infoBoxRect.top - (infoBoxRect.height / 2);
            let parentTop = parentRect.top;
            let parentBottom = parentRect.bottom;

            if (infoBoxTop < parentTop) {
              this.finalPosition = 'bottom-left';
            } else if (infoBoxTop + infoBoxRect.height > parentBottom) {
              this.finalPosition = 'top-left';
            } else {
              this.finalPosition = this.position;
            }
          } else if (this.position === 'top') {
            let infoBoxTop = infoBoxRect.top - (infoBoxRect.height / 2);
            let parentTop = parentRect.top;

            if (infoBoxTop < parentTop) {
              this.finalPosition = 'bottom';
            } else {
              this.finalPosition = this.position;
            }
          } else if (this.position === 'bottom') {
            let infoBoxTop = infoBoxRect.top - (infoBoxRect.height / 2);
            let parentBottom = parentRect.bottom;

            if (infoBoxTop + infoBoxRect.height > parentBottom) {
              this.finalPosition = 'top';
            } else {
              this.finalPosition = this.position;
            }
          }
        } else {
          this.finalPosition = this.position;
        }
      }, 50);
    },
    hide (ignoreEvent = false) {
      if (ignoreEvent) {
        return;
      }

      this.isVisible = false;
      this.finalPosition = '';
    }
  },
  beforeDestroy () {
    this.$bus.$off('body-clicked', this.hide);
    this.$bus.$off('popup-clicked', this.hide);
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.info-box {
  background: $color-light;
  border-radius: $border-radius-small;
  box-shadow: 0 5px 32px rgba(0, 0, 0, .25);
  left: 50%;
  opacity: 0;
  position: absolute;
  transform: translateX(-50%);
  transition: opacity .25s ease-out;
  width: 24rem;
  white-space: normal;
  z-index: 10;

  &.is-visible {
    opacity: 1;
  }

  &:after {
    border: 1rem solid $color-light;
    border-bottom-color: transparent;
    border-left-color: transparent;
    border-right-color: transparent;
    bottom: -2rem;
    content: "";
    height: 1rem;
    left: 50%;
    position: absolute;
    transform: translateX(-50%);
    width: 2rem;
  }

  &.is-right {
    transform: translateY(-50%);

    &:after {
      border: 1rem solid $color-light;
      border-bottom-color: transparent;
      border-left-color: transparent;
      border-top-color: transparent;
      bottom: -2rem;
      content: "";
      height: 2rem;
      left: -2rem;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      width: 1rem;
    }
  }

  &.is-bottom,
  &.is-bottom-left {
    &:after {
      border-bottom-color: $color-light;
      border-top-color: transparent;
      top: -2rem;
    }
  }

  &.is-bottom-left {
    border-top-left-radius: 0;
    left: 0%;
    transform: translateX(0%);

    &:after {
      left: 0;
      transform: translateX(0);
    }
  }

  &.is-top-left {
    border-bottom-left-radius: 0;
    left: 0%;
    transform: translateX(0%);

    &:after {
      left: 0;
      transform: translateX(0);
    }
  }

  &.is-bigger {
    width: 32rem;
  }

  &.is-biggest {
    width: 48rem;
  }

  &-content {
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    padding: 2rem;

    /deep/ a {
      color: $color-link!important;
      text-decoration: none;
    }
  }

  &.has-height-limit {
    .info-box-content {
      max-height: 32rem;
    }
  }

  hr {
    border-bottom: 1px solid $color-border;
    border-top: none;
  }

  /* periods styling */
  &-period {
    margin-top: 1rem;
    text-align: center;

    &:first-child {
      margin-top: 0;
    }

    &-current {
      color: $color-notice;
      font-weight: bold;
    }
  }

  /* parameters styling */
  &-parameter {
    margin-top: 1rem;

    &:first-child {
      margin-top: 0;
    }
  }
}

@media (max-width: 540px) {
  .info-box {
    width: 18rem;

    .info-box-content {
      padding: 1rem;
    }

    &.is-bigger {
      width: 18rem;
    }

    &.is-biggest {
      width: 18rem;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/common/InfoBox.vue